// web/src/shell/UpstreamHealthBanner.tsx
//
// Top-of-shell banner that shows when the upstream news-analyzer-api
// is unreachable. Renders nothing while healthy or while the first
// liveness check is still in flight, so we don't flicker on initial
// load.
//
// User can dismiss the banner; it reappears automatically next time
// the status changes (e.g. recovers and then goes down again).
import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { useUpstreamHealth } from "@/api/health";

export function UpstreamHealthBanner() {
  const { status } = useUpstreamHealth();
  const { t } = useTranslation("common");
  const [dismissed, setDismissed] = useState(false);
  const [lastSeenStatus, setLastSeenStatus] = useState(status);

  // Reset dismissed state on every status transition. If the service
  // recovers and then breaks again, the user gets a fresh banner.
  useEffect(() => {
    if (status !== lastSeenStatus) {
      setDismissed(false);
      setLastSeenStatus(status);
    }
  }, [status, lastSeenStatus]);

  if (status !== "unreachable" || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-3 border-b border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="flex-1">
        <Trans
          i18nKey="common:upstreamBanner.message"
          components={{ strong: <strong className="font-medium" /> }}
        />
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t("upstreamBanner.dismiss")}
        className="ms-2 rounded p-1 hover:bg-destructive/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-destructive"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
